/**
 * FeaturedPluckStrip — additive Cutest Pluck top-3 winners strip.
 *
 * Mounted on the landing page (`Landing.jsx`) below the existing
 * `dog-owners-section` (`landing_family` slot) and above the shared
 * footer. LOCKED constraints from the Phase C dispatch:
 *   - Does NOT touch `landing_hero` or `landing_family` slots.
 *   - Renders NOTHING (returns null, no DOM) when the public
 *     `/featured-photos?slot_prefix=landing_top` endpoint returns
 *     zero rows — i.e. before winners are declared. This is what
 *     keeps the pre-contest landing page identical to HEAD.
 *   - When 3 rows are present (or fewer during a partial rank
 *     assignment), renders a horizontal card row with rank ribbon,
 *     photo, dog_name, caption; each card links to the entry
 *     detail page.
 *
 * No inline hex colors. Uses existing brand tokens (`--pp-purple`,
 * `--pp-orange`, `--pp-v3-bg`, `--pp-shadow`, `.pp-wrap`) so the
 * Family L guard is happy.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy } from "lucide-react";

const RANK_RIBBON = {
  1: "1st Place",
  2: "2nd Place",
  3: "3rd Place",
};

export default function FeaturedPluckStrip() {
  const [rows, setRows] = useState(null); // null=loading, [] = empty

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(
          "/public/featured-photos?slot_prefix=landing_top",
        );
        if (!cancelled) setRows(r.data?.featured_photos || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Render nothing while loading OR when API returns empty — this
  // is the LOCKED "no DOM before winners" invariant.
  if (rows == null || rows.length === 0) return null;

  return (
    <section
      style={{ background: "var(--pp-v3-bg)" }}
      data-testid="featured-pluck-strip"
    >
      <div className="pp-wrap py-16 md:py-20">
        <div className="mb-8 flex flex-col items-center text-center">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider"
            style={{ background: "var(--pp-purple)", color: "#fff" }}
          >
            <Trophy className="h-3 w-3" aria-hidden="true" />
            Contest winners
          </span>
          <h2
            className="mt-3 font-heading text-3xl font-bold sm:text-4xl"
            style={{ color: "var(--pp-text)" }}
            data-testid="featured-pluck-strip-heading"
          >
            Meet the Cutest Plucks on the Block
          </h2>
        </div>
        <ul
          className="grid grid-cols-1 gap-6 md:grid-cols-3"
          data-testid="featured-pluck-strip-list"
        >
          {rows.map((r) => (
            <li
              key={r.slot}
              className="overflow-hidden rounded-3xl"
              style={{ background: "#fff", boxShadow: "var(--pp-shadow)" }}
              data-testid={`featured-pluck-card-${r.winner_rank}`}
            >
              <Link
                to={`/cutest-pluck/entries/${r.entry_id}`}
                className="block"
                data-testid={`featured-pluck-card-link-${r.winner_rank}`}
              >
                <div className="relative aspect-[4/3] w-full">
                  {r.photo_url ? (
                    <img
                      src={r.photo_url}
                      alt={r.dog_name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="h-full w-full"
                      style={{ background: "var(--pp-v3-bg)" }}
                    />
                  )}
                  <div
                    className="absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider"
                    style={{ background: "var(--pp-purple)", color: "#fff" }}
                    data-testid={`featured-pluck-ribbon-${r.winner_rank}`}
                  >
                    {RANK_RIBBON[r.winner_rank] || `Rank ${r.winner_rank}`}
                  </div>
                </div>
                <div className="p-5">
                  <p
                    className="font-heading text-xl font-bold"
                    style={{ color: "var(--pp-text)" }}
                    data-testid={`featured-pluck-name-${r.winner_rank}`}
                  >
                    {r.dog_name}
                  </p>
                  {r.caption && (
                    <p
                      className="mt-1 line-clamp-2 text-sm"
                      style={{ color: "var(--muted-foreground, #6b7280)" }}
                    >
                      {r.caption}
                    </p>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
